// features/settlement/settlementApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const settlementApi = createApi({
  reducerPath: "settlementApi",
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_API + "financial/settlement",
    prepareHeaders: (headers, { getState }) => {
      const token = getState()?.userSlice?.token;
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: ["Settlement", "MySettlement"],
  endpoints: (builder) => ({
    // User endpoints
    getMySettlements: builder.query({
      query: ({ page = 1, limit = 30, status = "" }) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (status) params.append("status", status);
        return {
          url: `/me?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({
                type: "MySettlement",
                id: _id,
              })),
              { type: "MySettlement", id: "LIST" },
            ]
          : [{ type: "MySettlement", id: "LIST" }],
    }),

    getMySettlementById: builder.query({
      query: ({ id }) => ({
        url: `/me/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, { id }) => [{ type: "MySettlement", id }],
    }),

    captureSettlementPayment: builder.mutation({
      query: ({ id }) => ({
        url: `/me/pamentcheck/${id}`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "MySettlement", id },
        { type: "MySettlement", id: "LIST" },
      ],
    }),

    // Admin endpoints
    getAdminSettlements: builder.query({
      query: ({
        page = 1,
        limit = 30,
        status = "",
        search = "",
        orderId = "",
        customerId = "",
        providerId = "",
        categoryId = "",
      }) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (status) params.append("status", status);
        if (search) params.append("search", search);
        if (orderId) params.append("orderId", orderId);
        if (customerId) params.append("customerId", customerId);
        if (providerId) params.append("providerId", providerId);
        if (categoryId) params.append("categoryId", categoryId);
        return {
          url: `/admin?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({
                type: "Settlement",
                id: _id,
              })),
              { type: "Settlement", id: "LIST" },
            ]
          : [{ type: "Settlement", id: "LIST" }],
    }),

    getAdminSettlementById: builder.query({
      query: ({ id }) => ({
        url: `/admin/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, { id }) => [{ type: "Settlement", id }],
    }),

    updateSettlementStatus: builder.mutation({
      query: ({ id, status, note = "" }) => ({
        url: `/admin/${id}/status`,
        method: "PATCH",
        body: { status, note },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Settlement", id },
        { type: "Settlement", id: "LIST" },
        { type: "MySettlement", id },
        { type: "MySettlement", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetMySettlementsQuery,
  useGetMySettlementByIdQuery,
  useCaptureSettlementPaymentMutation,
  useGetAdminSettlementsQuery,
  useGetAdminSettlementByIdQuery,
  useUpdateSettlementStatusMutation,
} = settlementApi;
